import styled from 'styled-components';
import { useListFleetModelRevisionsQuery } from '@shared/api';
import { pluralizeRu } from '@shared/lib/formatFile';
import { SkeletonLine } from '@shared/ui/Skeleton';
import {
  DiffList,
  FleetChip,
  fleetLeading,
  fleetType,
  formatDateTime,
  revisionActionLabel,
  revisionSourceLabel,
} from '@entities/fleet';
import { Notice, TextButton } from './ui';

const Timeline = styled.ol`
  position: relative;
  margin: 0;
  padding: 0;
  list-style: none;

  &::before {
    content: '';
    position: absolute;
    top: 8px;
    bottom: 8px;
    left: 4px;
    width: 1px;
    background: ${({ theme }) => theme.colors.border};
  }
`;

const Entry = styled.li`
  position: relative;
  padding: ${({ theme }) => `0 0 ${theme.spacing(6)} ${theme.spacing(6)}`};

  &::before {
    content: '';
    position: absolute;
    top: 6px;
    left: 0;
    width: 9px;
    height: 9px;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.backgroundElevated};
    box-shadow: inset 0 0 0 2px ${({ theme }) => theme.colors.primary};
  }

  &:last-child {
    padding-bottom: 0;
  }
`;

const EntryHead = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => `${theme.spacing(1.5)} ${theme.spacing(3)}`};
  margin-bottom: ${({ theme }) => theme.spacing(2)};
  font-size: ${fleetType.body};
  line-height: ${fleetLeading.tight};

  & > time {
    color: ${({ theme }) => theme.colors.text.secondary};
    font-variant-numeric: tabular-nums;
  }

  & > strong {
    font-size: ${fleetType.base};
    font-weight: 600;
  }
`;

const Muted = styled.p`
  margin: 0;
  font-size: ${fleetType.body};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

/** Журнал правок модели: новые сверху, «было → стало» с подписями паспорта. */
export function JournalPanel({ modelId }: { modelId: string }) {
  const { data, isLoading, isError, refetch } = useListFleetModelRevisionsQuery(modelId);

  if (isLoading) {
    return (
      <div aria-busy="true" aria-label="Загрузка журнала">
        <SkeletonLine $width="40%" />
        <SkeletonLine $width="90%" />
        <SkeletonLine $width="75%" />
      </div>
    );
  }
  if (isError) {
    return (
      <Notice $tone="danger" role="alert">
        <span className="text">Не удалось загрузить журнал правок.</span>
        <TextButton type="button" onClick={() => void refetch()}>
          Повторить
        </TextButton>
      </Notice>
    );
  }
  const items = data?.items ?? [];
  if (items.length === 0) return <Muted>Правок пока нет.</Muted>;

  return (
    <Timeline aria-label="Журнал правок">
      {items.map((r) => {
        const fields = Object.keys(r.diff).length;
        const create = r.action === 'create';
        return (
          <Entry key={r.id}>
            <EntryHead>
              <time dateTime={r.changed_at}>{formatDateTime(r.changed_at)}</time>
              <strong>{revisionActionLabel(r.action)}</strong>
              <FleetChip $tone={r.source === 'import' ? 'info' : 'neutral'}>
                {revisionSourceLabel(r.source)}
              </FleetChip>
              {r.changed_by_name && <Muted as="span">{r.changed_by_name}</Muted>}
              {create && fields > 0 && (
                <Muted as="span">
                  заполнено {fields} {pluralizeRu(fields, ['поле', 'поля', 'полей'])}
                </Muted>
              )}
            </EntryHead>
            {fields > 0 ? (
              <DiffList
                diff={r.diff}
                mode={create ? 'create' : 'change'}
                limit={create ? 6 : 12}
                label={`${revisionActionLabel(r.action)}, ${formatDateTime(r.changed_at)}`}
              />
            ) : (
              <Muted>Поля не менялись.</Muted>
            )}
          </Entry>
        );
      })}
    </Timeline>
  );
}
